import {
  Activity,
  Braces,
  Building2,
  Cable,
  ChevronRight,
  CircleGauge,
  LogOut,
  Menu,
  ScrollText,
  Settings,
  ShieldCheck,
  Users,
  X,
} from "lucide-react";
import {
  type FormEvent,
  type PropsWithChildren,
  type ReactNode,
  useState,
} from "react";
import {
  NavLink,
  Outlet,
  useLocation,
} from "react-router-dom";
import { useAuth } from "./auth";

const NAV_LINKS = [
  { to: "/", label: "Overview", icon: CircleGauge, end: true },
  { to: "/tenants", label: "Tenants", icon: Building2 },
  { to: "/integrations", label: "Integrations", icon: Cable },
  { to: "/data", label: "Flow data", icon: Braces },
  { to: "/system", label: "System", icon: Activity },
  { to: "/audit", label: "Audit trail", icon: ScrollText },
  { to: "/settings", label: "Settings", icon: Settings },
  { to: "/users", label: "Operators", icon: Users },
];

export function Shell() {
  const { session, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <div className="app-shell">
      <button
        type="button"
        className="mobile-nav-button"
        aria-label="Open navigation"
        onClick={() => setOpen(true)}
      >
        <Menu size={20} />
      </button>
      {open && (
        <button
          type="button"
          className="nav-scrim"
          aria-label="Close navigation"
          onClick={() => setOpen(false)}
        />
      )}
      <aside className={`nav-rail ${open ? "nav-rail--open" : ""}`}>
        <div className="brand-lockup">
          <div className="brand-junction" aria-hidden="true">
            <span />
            <span />
            <span />
          </div>
          <div>
            <strong>Nexus</strong>
            <small>Control room</small>
          </div>
          <button type="button" className="nav-close" onClick={() => setOpen(false)} aria-label="Close navigation">
            <X size={18} />
          </button>
        </div>
        <nav aria-label="Primary">
          {NAV_LINKS.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) => isActive ? "nav-link nav-link--active" : "nav-link"}
              onClick={() => setOpen(false)}
            >
              <Icon size={17} strokeWidth={1.8} />
              <span>{label}</span>
              <ChevronRight className="nav-link__chevron" size={14} />
            </NavLink>
          ))}
        </nav>
        <div className="operator-card">
          <div className="operator-card__avatar">
            {session?.user.display_name.slice(0, 2).toUpperCase()}
          </div>
          <div className="operator-card__identity">
            <strong>{session?.user.display_name}</strong>
            <span>{session?.user.role}</span>
          </div>
          <button type="button" className="icon-button" aria-label="Sign out" onClick={() => void logout()}>
            <LogOut size={17} />
          </button>
        </div>
      </aside>
      <main key={location.pathname} className="workspace">
        <Outlet />
      </main>
    </div>
  );
}

export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
}: {
  eyebrow: string;
  title: string;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    <header className="page-header">
      <div>
        <p className="eyebrow">{eyebrow}</p>
        <h1>{title}</h1>
        {description && <p className="page-header__description">{description}</p>}
      </div>
      {actions && <div className="page-actions">{actions}</div>}
    </header>
  );
}

export function Panel({
  title,
  meta,
  children,
  className = "",
}: PropsWithChildren<{ title?: string; meta?: ReactNode; className?: string }>) {
  return (
    <section className={`panel ${className}`}>
      {(title || meta) && (
        <header className="panel__header">
          {title && <h2>{title}</h2>}
          {meta && <div className="panel__meta">{meta}</div>}
        </header>
      )}
      {children}
    </section>
  );
}

export function StatusPill({ value }: { value?: string }) {
  const normalized = value?.toLowerCase() ?? "unknown";
  const positive = ["active", "success", "succeeded", "delivered", "ok", "healthy", "queued"];
  const negative = ["failed", "error", "dlq", "suspended", "unhealthy"];
  const caution = ["pending", "retry", "degraded", "configured", "paused", "archived"];
  const tone = positive.includes(normalized)
    ? "positive"
    : negative.includes(normalized)
      ? "negative"
      : caution.includes(normalized)
        ? "caution"
        : "neutral";

  return <span className={`status-pill status-pill--${tone}`}>{normalized.replaceAll("_", " ")}</span>;
}

export function EmptyState({
  title,
  body,
}: {
  title: string;
  body: string;
}) {
  return (
    <div className="empty-state">
      <ShieldCheck size={24} />
      <strong>{title}</strong>
      <p>{body}</p>
    </div>
  );
}

export function LoadingState({ label = "Reading Nexus state" }: { label?: string }) {
  return (
    <div className="loading-state" role="status">
      <span className="loading-state__line" />
      <span>{label}</span>
    </div>
  );
}

export function ErrorState({ error }: { error: unknown }) {
  const message = error instanceof Error ? error.message : "Nexus could not load this view.";
  return (
    <div className="error-state" role="alert">
      <strong>Data could not be loaded</strong>
      <p>{message}</p>
    </div>
  );
}

export function Metric({
  label,
  value,
  tone = "default",
}: {
  label: string;
  value: string | number;
  tone?: "default" | "positive" | "negative";
}) {
  return (
    <div className={`metric metric--${tone}`}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

export function EventRail({
  stages,
}: {
  stages: Array<{ label: string; value: string; state: "done" | "active" | "failed" | "idle" }>;
}) {
  return (
    <ol className="event-rail" aria-label="Event processing path">
      {stages.map((stage, index) => (
        <li className={`event-rail__stage event-rail__stage--${stage.state}`} key={stage.label}>
          <span className="event-rail__node">{index + 1}</span>
          <div>
            <small>{stage.label}</small>
            <strong>{stage.value}</strong>
          </div>
        </li>
      ))}
    </ol>
  );
}

export function JSONPreview({ value }: { value: unknown }) {
  return (
    <pre className="json-preview">
      <code>{JSON.stringify(value, null, 2)}</code>
    </pre>
  );
}

export function Modal({
  title,
  children,
  onClose,
}: PropsWithChildren<{ title: string; onClose(): void }>) {
  return (
    <div className="modal-layer" role="presentation">
      <button type="button" className="modal-scrim" onClick={onClose} aria-label="Close dialog" />
      <section className="modal" role="dialog" aria-modal="true" aria-label={title}>
        <header className="modal__header">
          <h2>{title}</h2>
          <button type="button" className="icon-button" onClick={onClose} aria-label="Close dialog">
            <X size={18} />
          </button>
        </header>
        {children}
      </section>
    </div>
  );
}

export function ConfirmForm({
  action,
  detail,
  confirmLabel,
  onConfirm,
  onCancel,
  busy,
}: {
  action: string;
  detail: string;
  confirmLabel: string;
  onConfirm(reason: string): Promise<void>;
  onCancel(): void;
  busy?: boolean;
}) {
  const [reason, setReason] = useState("");

  async function submit(event: FormEvent) {
    event.preventDefault();
    await onConfirm(reason);
  }

  return (
    <form className="stack-form" onSubmit={(event) => void submit(event)}>
      <p>{detail}</p>
      <label>
        Reason
        <textarea
          required
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder={`Why are you choosing to ${action.toLowerCase()}?`}
        />
      </label>
      <div className="form-actions">
        <button type="button" className="button button--ghost" onClick={onCancel}>Cancel</button>
        <button type="submit" disabled={busy} className="button button--primary">{confirmLabel}</button>
      </div>
    </form>
  );
}

